"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 65: Irem H-3001
 * ------------------------------------------------------------------ *
 * Daiku no Gen-san 2, Kaiketsu Yanchamaru 3 and Spartan X 2 are the only games
 * on this board. Two 8 KB PRG windows, eight 1 KB CHR windows, one mirroring
 * bit, and a 16-bit down counter that runs on CPU cycles rather than on
 * scanlines -- which is the part that has to be wired to the CPU clock and not
 * to the once-per-line hook.
 *
 * Register map follows https://www.nesdev.org/wiki/INES_Mapper_065:
 *   Mask $F007, so the register is picked by address bits 0-2 and the $x page.
 *
 *   $8000-$8007  PRG reg 0: 8 KB at $8000, or at $C000 when $9000 bit 7 is set
 *   $9000        bit 7: which of $8000/$C000 reg 0 drives; the other one is
 *                hard-wired to the second-to-last 8 KB bank
 *   $9001        bits 6-7: mirroring (0 vertical, 2 horizontal, 1/3 one-screen A)
 *   $9003        bit 7: IRQ enable; the write also acknowledges a pending IRQ
 *   $9004        copy the reload value into the counter; also acknowledges
 *   $9005        high 8 bits of the reload value
 *   $9006        low 8 bits of the reload value
 *   $A000-$A007  PRG reg 1: 8 KB at $A000
 *   $B000-$B007  CHR regs, 1 KB each, from $0000 to $1C00
 *
 * $E000-$FFFF is always the last 8 KB bank. On power-up reg 0 is $00 and reg 1
 * is $01, and the games rely on that: a H-3001 game that comes up with the
 * wrong bank at $8000 fetches its reset-time data from the wrong place.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  Mappers[65] = function (nes) {
    this.nes = nes;
  };

  Mappers[65].prototype = new Mappers[0]();

  // The H-3001 counts CPU cycles. Driven from the once-per-scanline hook it
  // would decrement about 113 times too slowly, so a game asking for a split
  // 200 cycles from now would get it a couple of scanlines late.
  Mappers[65].prototype.watchesCpuCycles = true;

  Mappers[65].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // PRG registers: $8000-$8007 and $A000-$A007.
    this.prgReg = [0, 1];

    // $9000 bit 7. 0 = reg 0 at $8000, 1 = reg 0 at $C000.
    this.prgLayout = 0;

    // $9001 bits 6-7, kept so a save round-trips what was written.
    this.mirroringReg = 0;

    // CHR registers: eight 1 KB windows.
    this.chrReg = [0, 0, 0, 0, 0, 0, 0, 0];

    // IRQ: 16-bit down counter, stops at zero, never reloads itself.
    this.irqEnabled = false;
    this.irqCounter = 0;
    this.irqReload = 0;
    this.irqPending = false;
  };

  Mappers[65].prototype.write = function (address, value) {
    if (address < 0x8000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    var page = (address >> 12) & 0x0f;
    var reg = address & 0x07;

    switch (page) {
      case 0x8:
        this.prgReg[0] = value;
        this.updatePrgBanks();
        return;

      case 0xa:
        this.prgReg[1] = value;
        this.updatePrgBanks();
        return;

      case 0xb:
        this.chrReg[reg] = value;
        this.updateChrBanks();
        return;

      case 0x9:
        switch (reg) {
          case 0x0:
            this.prgLayout = (value & 0x80) !== 0 ? 1 : 0;
            this.updatePrgBanks();
            return;

          case 0x1:
            this.mirroringReg = value;
            this.updateMirroring();
            return;

          case 0x3:
            this.irqEnabled = (value & 0x80) !== 0;
            this.acknowledgeIrq();
            return;

          case 0x4:
            this.irqCounter = this.irqReload;
            this.acknowledgeIrq();
            return;

          case 0x5:
            this.irqReload = (this.irqReload & 0x00ff) | ((value & 0xff) << 8);
            return;

          case 0x6:
            this.irqReload = (this.irqReload & 0xff00) | (value & 0xff);
            return;

          default:
            // $9002 and $9007 are not decoded.
            return;
        }

      default:
        // $C000-$FFFF is ROM, so a write there goes nowhere.
        return;
    }
  };

  Mappers[65].prototype.updatePrgBanks = function () {
    var rom = this.nes.rom;
    var banks8k = rom.romCount * 2;
    if (banks8k === 0) {
      return;
    }

    // The register holds an 8 KB bank number, so it wraps against the 8 KB
    // count. Wrapping against the 16 KB count instead halved the reachable half
    // of every ROM: bank 9 of a 16-bank image became bank 1.
    var reg0 = this.prgReg[0] % banks8k;
    var reg1 = this.prgReg[1] % banks8k;
    var secondLast = banks8k - 2;
    var last = banks8k - 1;

    if (this.prgLayout === 0) {
      this.load8kRomBank(reg0, 0x8000);
      this.load8kRomBank(reg1, 0xa000);
      this.load8kRomBank(secondLast, 0xc000);
    } else {
      this.load8kRomBank(secondLast, 0x8000);
      this.load8kRomBank(reg1, 0xa000);
      this.load8kRomBank(reg0, 0xc000);
    }
    this.load8kRomBank(last, 0xe000);
  };

  Mappers[65].prototype.updateChrBanks = function () {
    var rom = this.nes.rom;
    var banks1k = rom.vromCount * 4;

    for (var i = 0; i < 8; i++) {
      var bank = banks1k === 0 ? 0 : this.chrReg[i] % banks1k;
      this.load1kVromBank(bank, i * 0x400);
    }
  };

  Mappers[65].prototype.updateMirroring = function () {
    switch ((this.mirroringReg >> 6) & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      default:
        // 1 and 3 are the same single-screen arrangement, wired to the lower
        // name table.
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING);
        break;
    }
  };

  // One burst of CPU cycles. The counter runs only while enabled and stops at
  // zero, so a burst that overshoots lands on zero rather than wrapping past it.
  Mappers[65].prototype.clockCpuCycles = function (cycles) {
    if (!this.irqEnabled || this.irqCounter === 0) {
      return;
    }

    if (cycles >= this.irqCounter) {
      this.irqCounter = 0;
      this.raiseIrq();
    } else {
      this.irqCounter -= cycles;
    }
  };

  Mappers[65].prototype.raiseIrq = function () {
    if (this.irqPending) {
      return;
    }
    this.irqPending = true;
    this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
  };

  // A write to $9003 or $9004 clears a pending IRQ. The counter itself is left
  // alone: it is only restarted by the write to $9004, and only turned on and
  // off by $9003.
  Mappers[65].prototype.acknowledgeIrq = function () {
    this.irqPending = false;
  };

  Mappers[65].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Irem H-3001: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // The power-on values the games depend on.
    this.prgReg[0] = 0;
    this.prgReg[1] = 1;

    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();

    this.resetIrqHandler();
  };

  Mappers[65].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgReg = this.prgReg.slice();
    s.prgLayout = this.prgLayout;
    s.mirroringReg = this.mirroringReg;
    s.chrReg = this.chrReg.slice();
    s.irqEnabled = this.irqEnabled;
    s.irqCounter = this.irqCounter;
    s.irqReload = this.irqReload;
    s.irqPending = this.irqPending;
    return s;
  };

  Mappers[65].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgReg = s.prgReg.slice();
    this.prgLayout = s.prgLayout;
    this.mirroringReg = s.mirroringReg;
    this.chrReg = s.chrReg.slice();
    this.irqEnabled = s.irqEnabled;
    this.irqCounter = s.irqCounter;
    this.irqReload = s.irqReload;
    this.irqPending = s.irqPending;
    // The CPU and PPU keep their own copies of the banks, so re-derive them.
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();
  };
}

module.exports = { register: register };
